import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { IntentStatisticsDetail, DictType } from "@/model";
import QuestionIcon from "@/assets/svgs/question_mark.svg";


interface IntentStatTableProps {
    data?: DictType<IntentStatisticsDetail>;
}

const IntentStatTable: React.FC<IntentStatTableProps> = ({ data }) => {
    const intl = useIntl();

    const { labels, series, percentSeries, noClassificationCnt, noClassificationPercent } = useMemo(() => {
        if (!data) {
            return { labels: [], series: [], percentSeries: [], noClassificationCnt: 0, noClassificationPercent: "0" };
        }
        const total = Object.values(data).reduce((sum, item) => sum + item.cnt, 0);

        const classifiedData = Object.entries(data).filter(([key]) => key !== "no classification");
        const topFive = classifiedData.sort(([, a], [, b]) => b.cnt - a.cnt).slice(0, 5);
        const labels = topFive.map(item => item[0]);
        const series = topFive.map(item => item[1].cnt);
        const percentSeries = series.map(num => total ? ((num / total) * 100).toFixed(0) : "0");

        const noClassificationCnt = data["no classification"]?.cnt ?? 0;
        const noClassificationPercent = total ? ((noClassificationCnt / total) * 100).toFixed(0) : "0";

        return { labels, series, percentSeries, noClassificationCnt, noClassificationPercent };
    }, [data]);

    if (!data || labels.length === 0) {
        return (
            <div className="text-black-400 text-[14px] font-[400]">
                {intl.formatMessage({ id: "i02016" })}
            </div>
        );
    }
    return (
        <div className="size-full p-6 grid grid-cols-[40px_1fr_max-content] gap-x-4 gap-y-4">
            <div className="px-4 py-1 h-[260px] col-span-3 grid grid-cols-subgrid grid-rows-[repeat(5,minmax(0,1fr))] rounded-[6px] bg-chat42-5">
                {labels.slice(0, 5).map((label, index) => (
                    <div key={index}
                         className={classNames(
                             "col-span-3 grid grid-cols-subgrid h-full items-center justify-between border-b border-black-200",
                             index === 4 && "border-b-0",
                         )}
                    >
                        <div className="ml-auto size-5.5 flex items-center justify-center rounded-[4px] bg-chat42-20 text-black-500 text-[12px] font-[600]">
                            {index + 1}
                        </div>
                        <div className="text-[14px] font-[500] text-black-500">
                            {label}
                        </div>
                        <div className="text-[12x] font-[400] text-black-500 tabular-nums">
                            {`${series[index]} (${percentSeries[index]}%)`}
                        </div>
                    </div>
                ))}
            </div>

            <div className="px-4 col-span-3 w-full grid grid-cols-subgrid items-center">
                <img src={QuestionIcon} alt="question mark"
                     className="ml-auto size-4.5"
                />
                <div className="text-[14px] font-[500] text-black-500">
                    {intl.formatMessage({ id: "i02028" })}
                </div>

                <div className="text-[12x] font-[400] text-black-500 tabular-nums">
                    {`${noClassificationCnt} (${noClassificationPercent}%)`}
                </div>
            </div>
        </div>
    );
};

export default IntentStatTable;
